import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Badge } from "@/components/ui/badge";
import { Music, Guitar, Sparkles, Users, Calendar, Award } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useState } from "react";
import collageImage from "@/assets/musikonzept-collage.jpg";
import danielSommer from "@/assets/dozenten/daniel-sommer.jpg";
import nicoleKnorr from "@/assets/dozenten/nicole-knorr.jpg";
import katiBreuer from "@/assets/dozenten/kati-breuer.jpg";
import luciaRuf from "@/assets/dozenten/lucia-ruf.jpg";
import anneLueken from "@/assets/dozenten/anne-lueken.jpg";
import andreaNanke from "@/assets/dozenten/andrea-nanke.jpg";
import richardHaus from "@/assets/dozenten/richard-haus.jpg";
import cordulaTornow from "@/assets/dozenten/cordula-tornow.jpg";
import astridHauke from "@/assets/dozenten/astrid-hauke.jpg";
import biancaBaumgart from "@/assets/team/bianca-baumgart.jpg";
import benjaminMueller from "@/assets/team/benjamin-mueller.jpg";
import christinaSchmitz from "@/assets/team/christina-schmitz.jpg";
import nicolaPilger from "@/assets/team/nicola-pilger.jpg";
import micThieme from "@/assets/team/mic-thieme.jpg";
import stephenJanetzko from "@/assets/freunde/stephen-janetzko.jpg";
import donikkl from "@/assets/freunde/donikkl.jpg";
import robertMetcalf from "@/assets/freunde/robert-metcalf.jpg";
import franziskaBinder from "@/assets/freunde/franziska-binder.jpg";
import simoneLudwig from "@/assets/freunde/simone-ludwig.jpg";
import sandraLierz from "@/assets/freunde/sandra-lierz.jpg";
import leonardHahn from "@/assets/freunde/leonard-hahn.jpg";
import drKlingDrKlang from "@/assets/freunde/dr-kling-dr-klang.jpg";
import dirkScheele from "@/assets/freunde/dirk-scheele.jpg";
import ralfTrautner from "@/assets/freunde/ralf-trautner.jpg";
import franziskaWalter from "@/assets/freunde/franziska-walter.jpg";
import lauraIsabelBiastoch from "@/assets/freunde/laura-isabel-biastoch.jpg";
import helenSimicev from "@/assets/freunde/helen-simicev.jpg";
import frisbiJones from "@/assets/freunde/frisbi-jones.jpg";
import suliPushban from "@/assets/freunde/suli-pushban.jpg";
import idaDanzberg from "@/assets/freunde/ida-danzberg.jpg";
import andreasSchoba from "@/assets/freunde/andreas-schoba.jpg";
import rolfZuckowski from "@/assets/freunde/rolf-zuckowski.jpg";
import wolfgangHering from "@/assets/freunde/wolfgang-hering.jpg";
import silkeKratzsch from "@/assets/freunde/silke-kratzsch.jpg";
import claudiaGundlach from "@/assets/freunde/claudia-gundlach.jpg";
import doerteLangwald from "@/assets/freunde/doerte-langwald.jpg";
import SEO from "@/components/SEO";
import { FACHKRAEFTE_KURZ, FACHKRAEFTE_TEXT, KURSE_ZAHL, LEKTIONEN_KURZ } from "@/config/webseiteFakten";

const timelineData = [
  {
    year: "1999",
    icon: Guitar,
    title: "Die Anfänge: gitarrissimo® und Gitarrenunterricht in Kitas",
    description: "MusiKonzept wurde von Gitarrist und Dipl. Sozialpädagoge Daniel Sommer unter dem Namen 'gitarrissimo' gegründet, um die Kinderliedbegleitung auf der Gitarre für pädagogische Fachkräfte zugänglich zu machen.",
    highlight: "10.000 Erzieher geschult",
    color: "from-primary to-primary/70"
  },
  {
    year: "2013",
    icon: Sparkles,
    title: "Innovationen und Erweiterung des Angebots",
    description: "Entwicklung der C-Gitarre, speziell angepasst an die Bedürfnisse des pädagogischen Einsatzes. Ergänzend dazu wurden maßgeschneiderte Liederbücher und Unterrichtsmaterialien entwickelt.",
    highlight: "C-Gitarre & MusiKreativ",
    color: "from-accent to-accent/70"
  },
  {
    year: "2018",
    icon: Music,
    title: "Kreative Programme",
    description: "Erweiterung des Portfolios durch kreative Programme wie 'MusiKreativ', das den Einsatz von Boomwhackers, Bodypercussion und weiteren Orff-Instrumenten im pädagogischen Alltag fördert.",
    highlight: "Portfolio-Erweiterung",
    color: "from-primary/80 to-accent/80"
  },
  {
    year: "2019",
    icon: Calendar,
    title: "Der Schritt in die digitale Welt",
    description: "Gründung der Onlineakademie - ein bedeutender Schritt, um musikpädagogische Lerninhalte in digitaler Form verfügbar zu machen und Fachkräfte deutschlandweit zu erreichen.",
    highlight: "Onlineakademie gegründet",
    color: "from-accent/80 to-primary/60"
  },
  {
    year: "2020",
    icon: Award,
    title: "Corona-Pandemie & Modernisierung",
    description: "Das Angebot wurde konsequent modernisiert und erweitert. Regelmäßige MusiKonzeptage und monatliche Kreativpakete ermöglichen es, Musikpädagogik lebendige in den virtuellen Raum zu übertragen.",
    highlight: "Digitale Transformation",
    color: "from-primary to-accent"
  },
  {
    year: "Heute",
    icon: Users,
    title: "MusiKonzept heute",
    description: `Eine musikpädagogische Plattform mit über ${KURSE_ZAHL} Online-Kursen, Ausbildungen mit Zertifikat, monatlichen kreativen Impulsen, Miko als Helfer für die Praxis und einer Online-Musikschule.`,
    highlight: `${FACHKRAEFTE_KURZ} Fachkräfte · ${LEKTIONEN_KURZ} Lektionen`,
    color: "from-accent to-primary"
  }
];

const dozenten = [
  {
    name: "Daniel Sommer",
    role: "Dipl.-Soz.Päd. | Gründer & Leiter",
    email: "daniel.sommer@musikonzept.de",
    image: danielSommer,
    bio: "Dipl. Sozialpädagoge Daniel Sommer hat Musik im Blut.\n\nEr ist Gitarrist, Gitarrenlehrer, Autor und Gründer von gitarrissimo® und MusiKonzept, einem Lernportal rund um das Thema Musikpädagogik.\n\nEr gibt bundesweit Fortbildungen und Workshops, leitet Webinare und ist gefragter Dozent auf Kongressen und Tagungen."
  },
  {
    name: "Nicole Knorr",
    role: "Dozentin | Nürnberg",
    email: "nicole.knorr@musikonzept.de",
    image: nicoleKnorr,
    bio: "Nicole ist Musikpädagogin. Nach ihrem Studium der Elementaren Musikpädagogik (EMP) und Blockflöte an der Hochschule für Musik in Nürnberg gründete sie 2004 ihr eigenes Klangstudio27 in dem sie Gitarren-, Ukulele- und Blockflötenunterricht, sowie Kurse im elementaren Bereich (Musikalische Eltern-Kind-Kurse, Musikalische Früherziehung, Instrumentenkarussell, Trommelissimo, uvm.) anbietet. Außerdem arbeitet sie in div. Kitas und Grundschulen.\n\nSeit 2016 ist sie Dozentin bei gitarrissimo und war im Frühjahr 2020 beim Aufbau der Plattform von MusiKonzept beteiligt und bietet dort vielfältige Online-Fortbildungen, Seminare und Webinare an."
  },
  {
    name: "Kati Breuer",
    role: "Dozentin | Bremen",
    email: "mail@katibreuer.de",
    image: katiBreuer,
    bio: "Mein Name ist Kati Breuer, ich bin Kinderliedermacherin, Autorin und Musikkursleiterin aus Stuhr bei Bremen.\n\nWenn ich eins aus meiner langjährigen Erfahrung aus der musikpädagogischen Arbeit weiß, dann das: Alle Kinder lieben Musik! Da wird gesungen, mit dem Popo gewackelt und getanzt, jeder Tisch wird zur Trommel und jede Brotdose zur Rassel. Und wenn man dabei nicht alleine ist, macht es doch gleich noch mehr Spaß. Was liegt also näher, als sich zusammen mit Liedern, Versen, Instrumentenspielen (und mehr) zu beschäftigen?\n\nIn meinen Musikkursen erleben Pädagogen, Eltern und Kinder seit vielen Jahren, wie das gemeinsame musikalische Tun das Familienleben bereichert und die Beziehung stärkt. Endlich gibt es diese Möglichkeit nun auch online: Mit der MusiKonzept Mitgliedschaft Plus mit mir als Dozentin werdet ihr das ganze Jahr über versorgt mit vielfältigen musikalischen Ideen."
  },
  {
    name: "Lucia Ruf",
    role: "Dozentin | Konstanz",
    image: luciaRuf,
    bio: "Lucia Ruf ist Kinderliedermacherin, Erzieherin und Autorin. Sie lebt mit ihrer Familie am wunderschönen Bodensee.\n\nLucia Ruf's Leben ist voller Musik und diese Liebe zur Musik möchte sie an Kinder weitergeben. Deshalb ist es ihr wichtig, dass ihre Kinderlieder den Alltag der Kinder aufgreifen und widerspiegeln.\n\nDie Lieder haben einen hohen Aufforderungscharakter und lassen sich in der Praxis mit Kindern leicht gestalten und umsetzen. Neben dem Liederschreiben gibt sie Fortbildungen, Webinare und Kita-Konzerte."
  },
  {
    name: "Anne Lüken",
    role: "Dozentin | Oldenburg",
    image: anneLueken,
    bio: "Ich bin Anne. Mama, Musikerin, Musiklehrerin und Autorin. Ich bin Diplom Musiklehrerin im Fach Gitarre und arbeite seit 2007 an der Musikschule der Stadt Oldenburg. Ich unterrichte Gitarre, Geige und musikalische Früherziehung und war mit dem Programm \"Wir machen die Musik\" in Oldenburger Kitas unterwegs. An der Hochschule für Musik in Bremen war ich Dozentin für Gitarre in dem Projekt \"Musik bewegt Kinder\"."
  },
  {
    name: "Andrea Nanke",
    role: "Dozentin",
    image: andreaNanke,
    bio: "Andrea Nanke ist eine Musikpädagogin und Sängerin mit einem Abschluss in klassischem Gesang vom Konservatorium in Mainz. Seit 2003 unterrichtet sie Gesang, Klavier und elementare Musikpädagogik für Kinder, Jugendliche und Erwachsene. Als Dozentin für Gesang arbeitet sie seit 2009 mit Kindern und Jugendlichen von 7 bis 18 Jahren an der weltweit größten Freizeitschule für darstellende Künste. Ihr Fokus liegt auf einer kreativen Stimmbildung, die Bewegung und Spielfreude einbezieht.\n\nAndrea hat auch ein eigenes Studio namens \"RAUM FÜR KLANG\", wo sie elementare Musikpädagogik, Gesangs- und Instrumentalpädagogik anbietet. Sie war auch als Bildungsreferentin und Lehrbeauftragte tätig und wurde mit ihrem Programm \"LET'S JAM\" in das Kulturvermittlungsprogramm \"Jedem Kind seine Kunst\" des Landes Rheinland-Pfalz aufgenommen. Seit Herbst 2022 ist sie als Dozentin für das Netzwerk Kitamusik NRW, die Landesmusikakademie NRW und die Singschule Bonn tätig."
  },
  {
    name: "Richard Haus",
    role: "Dozent",
    website: "www.kimula.de",
    image: richardHaus,
    bio: "Richard Haus macht Musik für Kinder und ihre Erwachsenen (Richards Kindermusikladen). Für Workshops, Kurse und Fortbildungen entwickelt er Sing-, Sprech-, Rhythmus- und Bewegungsspiele mit neuen Lernmethoden für Kinder von 3 - 8 Jahren."
  },
  {
    name: "Cordula Tornow",
    role: "Dozentin",
    website: "www.cordula-tornow.de",
    image: cordulaTornow,
    bio: "Abgeschlossene Studiengänge: Tanzpädagogik (2000), Physical Theater (2005), M.A. Kunstanaloges Coaching (2019)\n\nWeiterbildungen: Kreativer Kindertanz, Entspannungspädagogin, Rückenschullehrerin, Trauerbegleiterin\n\n2007-2022 freischaffende Künstlerin & Pädagogin mit eigener Tanz- und Theaterschule in Lübeck\n\n2012-2016 Leiterin einer staatlich anerkannten Berufsfachschule für Tanz, Sport sowie Bühnentanz\n\n2010 - heute Dozentin der Sommerakademie im Lübecker Theater\n\nseit 2021 Weiterbildungsangebote im Bereich Tanz & Bewegung\n\nseit 2024 bei Musikonzept\n\nGerade die Verbindung von Bewegung & Sprache ist für das Lernen existentiell. So kreiere ich neben Weiterbildungsangeboten auch immer wieder neue Konzepte um Kinder nachhaltig, kreativ und ganzheitlich zu fördern."
  },
  {
    name: "Astrid Hauke",
    role: "Dozentin",
    image: astridHauke,
    bio: "Astrid Haukes Liebe zum Singen geht in ihre früheste Kindheit zurück. Für die Gesangsbegleitung lernte sie Gitarre und Klavier. Heute ist sie vorwiegend mit Akkordeon und Ukulele unterwegs.\n\nIhre Begeisterung für verschiedene Kulturen und deren Musik führt die Humor-Expertin in ferne Länder und Regionen rund um Globus. So wird ihre musikalische Identität durch neue Einflüsse bereichert, die sie in ihren Musikproduktionen zum Ausdruck bringt. In Auftrags-Kompositionen, zum Beispiel für die Stadt Verl oder die Bremer Schlachte, spiegelt sich die Weltoffenheit der Künstlerin wieder.\n\nDie Singer/Songwriterin wurde mit ihrem Titel \"Tanzende Flammen\" mit dem Goldenen Award im internationalen Deutschmusik-Song-Contest ausgezeichnet. 2020 erhielt sie den Deutschen Kinderliederpreis."
  }
];

const bueroTeam = [
  {
    name: "Bianca Baumgart",
    role: "Buchhaltung",
    image: biancaBaumgart,
    bio: "Inhalt folgt..."
  },
  {
    name: "Benjamin Müller",
    role: "Webdesign & Technik",
    image: benjaminMueller,
    bio: "Inhalt folgt..."
  },
  {
    name: "Christina Schmitz",
    role: "Videoproduktion",
    image: christinaSchmitz,
    bio: "Inhalt folgt..."
  },
  {
    name: "Nicola Pilger",
    role: "Illustration",
    image: nicolaPilger,
    bio: "Inhalt folgt..."
  },
  {
    name: "Mic Thieme",
    role: "Notation",
    image: micThieme,
    bio: "Inhalt folgt..."
  }
];

const freundeMitwirkende = [
  {
    name: "Stephen Janetzko",
    role: "Kinderliedermacher, Autor, Verlag & Label",
    website: "www.kinderliederhits.de",
    image: stephenJanetzko,
    bio: "Mehr als 900 Lieder und über 50 CDs & jede Menge Liederbücher!"
  },
  {
    name: "DONIKKL",
    role: "Kinderliedermacher",
    website: "www.donikkl.com",
    image: donikkl,
    bio: "Die DONIKKL Crew bringt mit Ohrwürmern voller positiver Energie und jeder Menge Mitmach-Choreos nicht nur Zwerge, sondern auch Jugendliche und Erwachsene zum Ausflippen.\n\nReite mit Cowgirls und Cowboys durch den Wilden Westen, Hopple mit dem kleinen Hasen, der immer wieder ausbüchst, um die Wette, schwimme mit dem kleinen Haifisch, spring wie ein Känguru, geh mit Piraten auf Schatzsuche, tanze wie ein wildes Feuer, brülle wie ein Tiger und zeig, wie stark du bist, finde heraus, dass du genau so richtig bist, wie du bist und singe ganz laut \"Heut ist so ein schöner Tag!\""
  },
  {
    name: "Robert Metcalf",
    role: "Kinderliedermacher",
    website: "www.robert-metcalf.de",
    image: robertMetcalf,
    bio: "Der gebürtige Engländer wurde 1947 in Birmingham geboren und wuchs dort auf. Er kam 1973 nach Deutschland und lebt seitdem in Berlin. Zuvor hatte er in London und Paris Jura studiert. Nach einem Praktikum bei der Europäischen Gemeinschaft, absolviert er in den Siebzigerjahren eine Ausbildung als Sozialpädagoge und arbeitete in diesem Beruf – vornehmlich im Suchtbereich – bis 1990. Nebenbei trat er mit eigenen Songs in kleinen Westberliner Musikkneipen auf und wurde Mitte 1984 mit seinen ersten deutschsprachigen Liedern Preisträger beim Nürnberger Bardentreff.\n\n1990 gab Robert Metcalf seine Arbeit in einer Beratungsstelle für Drogenabhängige auf, um freiberuflich als Liedermacher/Songwriter tätig zu werden. Seitdem liegt sein beruflicher Schwerpunkt im Bereich Kinderlied, sein äußeres Erkennungsmerkmal: eine schwarze Melone. In den letzten Jahren tritt er zunehmend – vor allem mit seinem zweisprachigen Abendprogramm \"Englishman in Berlin\" – wieder vor einem Erwachsenenpublikum auf.\n\nNach Auftritten im Radio (RIAS Kinderfunk) und Fernsehen (\"Flimmerstunde\") in den Neunzigerjahren folgten zunehmend Live-Konzerte in Solo-, Duo- oder Bandbesetzung. Robert Metcalf hat Auftragsproduktionen für diverse Institutionen (u.a. Goethe Institut, Universal Music, Random House, Berliner Sportjugend) gemacht und hat inzwischen zahlreiche eigene CDs veröffentlicht, in deutscher und auch in seiner englischen Muttersprache. Seit 2007 ist er regelmäßig bei der \"Sendung mit dem Elefanten\" (Kinderkanal) zu sehen."
  },
  {
    name: "Franziska Binder",
    role: "Kinderliedermacherin",
    website: "www.musikonzept.de/courses/franziska-binder",
    image: franziskaBinder,
    bio: "Franziska ist Erzieherin und arbeitet in einem kleinen Kindergarten bei Ulm.\n\nSie bloggt in ihrer Freizeit im Netz über ihre Arbeit und Ideen, entwickelt und baut selbst Spielmaterial, schreibt Geschichten, Fingerspiele und seit einiger Zeit auch leidenschaftlich gerne neue Kinderlieder.\n\nZusätzlich zu ihrer Ausbildung zur Erzieherin hat sie eine Weiterbildung zur Musik-Mentorin absolviert."
  },
  {
    name: "Simone Ludwig",
    role: "Klavier- und Musikpädagogin, Songwriter und Autorin",
    website: "www.flohimohr.tv",
    image: simoneLudwig,
    bio: "Ein flohstarkes Hallo aus Köln! Ich bin Simone Ludwig und Du kennst mich vielleicht schon von meinem Youtubekanal \"Floh im Ohr TV\". Dort dreht sich alles um die wundervolle Welt der Musik. Genauso wie in meinem Leben.\n\nIch bin Klavier- und Musikpädagogin, Songwriter und Autorin. Mit viel Leidenschaft spiele ich seit meinem 8. Lebensjahr Klavier und habe das auch zu meinem Beruf gemacht. Ich gebe seit mittlerweile mehr als 20 Jahren Kindern und Erwachsenen Klavierunterricht. Bis vor ein paar Jahren habe ich zusätzlich noch Kurse in musikalischer Früherziehung (4-6 Jahre) und Frühförderung (1,5-3 Jahre) erteilt. Meine sozialpädagogische Ausbildung (staatl. anerkannte Erzieherin) und mein musikalischer Werdegang sind hierbei eine wundervolle Kombi.\n\nMir macht es großen Spaß anderen Menschen musikalische Dinge zu erklären. Das hat mich auch dazu bewogen zwei Bücher über \"Pit, den kleinen Floh\" (\"Das Tastenabenteuer\" und \"Das Streicherabenteuer\") zu schreiben. Eigene Lieder zu texten/komponieren ist eine weitere Leidenschaft von mir. So entstanden auch die gleichnamigen Hörspiele zu meinen Büchern. Besonders stolz bin ich über mein erstes Album \"Flohstarke Hits für Kita-Kids\". Es sind größtenteils Mitmach-Tanzlieder und ideal für den Einsatz in der Kita. Viele Lieder sind bereits praxiserprobt, da sie aus der Arbeit mit Kindern in der EMP entstanden sind."
  },
  {
    name: "Sandra Lierz",
    role: "Musikpädagogin & Erzieherin",
    image: sandraLierz,
    bio: "3 Kinder\nSeit 1998 staatlich anerkannte Erzieherin\n\nZusatz Qualifikationen:\nKursleiterin für indische Babymassage\nKursleiterin für \"Handling funktionelle Säuglingsgymnastik\"\nKindermassage\nEltern-Kind-Turnen\nmehrere Weiterbildungen in Psychomotorik\nseit 2009 Musikpädagogin\nMusikgarten und musikalische Früherziehung\nvon 2008-2017 selbstständig Nestwärme\nseit 2017 als Erzieherin und stellvertretende Leiterin bei der Stadt Wesseling\nWiederaufnahme der Nestwärme Sommer 2020"
  },
  {
    name: "Leonard Hahn",
    role: "Liedermacher, Autor, Produzent und Musikpädagoge",
    website: "www.lalaleo.de/lalaleo",
    image: leonardHahn,
    bio: "Ich bin Leonard Hahn alias Lalaleo – Liedermacher, Autor, Produzent und Musikpädagoge\n\nMeine Lieder für Kinder entstehen stets aus dem Effekt heraus. Oft im Freestyle, skizzenhaft – einfach aus dem Zufall heraus, so wie das Leben oft voller Überraschungen steckt. Das fühlt sich für mich gut an, besonders wenn es um das Musik machen geht – hier stecke ich meine ganze Leidenschaft hinein. Schon immer schreibe ich gerne Reime und Geschichten, spiele Schlagzeug und Gitarre und mache viel Sprechgesang. Durch viele Auftritte auf verschiedenen Kita-, Stadt- und Straßenfesten konnte ich im Live-Bereich in den vergangenen Jahren viele wertvolle Erfahrungen sammeln.\n\nSchon damals mit 20 Jahren war ich mit meinem Hip Hop-Projekt bundesweit unterwegs, spielte in vielen Locations und auf Festivals und schaffte es damals mit meinen ersten Musikvideos in die \"Heavy Rotation\" auf VIVA und VIVA Plus. Das kreative und spontane Texten und Komponieren von damals habe ich mir bewahrt. Auch heute bin ich mit dem Schreiben von Texten noch ständig im Fluss, ob Fachartikel, Bücher, einzelne Songs oder ganze Alben, alles hat für mich seinen Reiz. Momentan sitze ich im Kreativlabor und tüftle an neuen Projekten."
  },
  {
    name: "Dr. Kling & Dr. Klang",
    role: "Elementar Musikpädagoginnen",
    website: "www.drklingunddrklang.de",
    image: drKlingDrKlang,
    bio: "Als Dr. Kling & Dr. Klang stecken die beiden Elementar Musikpädagoginnen, Basak Bollmann und Julia Meiß, große und kleine ZuhörerInnen mit ihrer Begeisterung zur Musik an. Durch interaktive Kinderkonzerte, musikpädagogische Arbeit in der Musikschule und Publikation eines Kinderbuches mit Musik sind die beiden künstlerisch und pädagogisch aktiv unterwegs.\n\nZusätzlich inspirieren sie ErzieherInnen, MusikpädagogInnen, Eltern und Musikinteressierte mit kurzen Videosequenzen über alltägliche Musik- und Spielideen, eigene Kinderlieder, Verse, Bodypercussion und vieles mehr.\n\nWeitere Infos über sie und ihre Arbeit gibt es auf: www.drklingunddrklang.de und unter dem Account @dr.kling_dr.klang auf Instagram."
  },
  {
    name: "Dirk Scheele",
    role: "Musiktherapeut & Kinderliedermacher",
    website: "www.dirkscheelekinderlieder.de",
    image: dirkScheele,
    bio: "Dirk Scheele ist ein niederländischer Musiktherapeut und ein Vollzeitkünstler für Kinder zwischen 2 und 7 Jahren mit 3 Konzerten pro Woche in Theatern und Veranstaltungsorten in den Niederlanden.\n\nAuf dem niederländischen und belgischen Sender Nickelodeon und NickJr hat er sein eigenes tägliches Musikfernsehprogramm. Dirks niederländischer YouTube-Kanal für Kinder ist in Holland mit mehr als 50.000 Subs sehr beliebt.\n\n2018 veröffentlichte er seinen ersten Kinofilm und 2020 erhielt Dirk eine königliche Auszeichnung für seine freiwillige Arbeit für kranke und behinderte Kinder.\n\nIm Moment möchte Dirk seine deutschen Songs in ganz Deutschland, Österreich und darüber hinaus verbreiten."
  },
  {
    name: "Ralf Trautner",
    role: "Musiker & Musiklehrer für Elementare Musikerziehung",
    image: ralfTrautner,
    bio: "1975 – 2002\nLive Musiker in Europa und USA\n\n2002\nMusiklehrer für Elementare Musikerziehung\n\nMeine Arbeitsbereiche (Elementare Musikerziehung):\nEltern – Kind Gruppen\nKinder Gruppen von 0 – 6 Jahren\nSonderpädagogisches Förderzentrum\nKinder mit Fluchterfahrung\nWorkshops für Menschen die mit Kindern arbeiten\n\nMeine Musik-Arbeitsbereiche:\nLife Auftritte als Begleit-Musiker\nOn Tour mit eigenem Programm \"Musik für Kinder\"\n\nMeine Studio-Arbeitsbereiche:\narrangieren, komponieren, texten und Musik-Produktionen\n\nMeine Instrumente:\nAkustik Gitarren, Ukulele, E-Gitarre, Bass und Gesang."
  },
  {
    name: "Franziska Walter",
    role: "Sängerin & Musikpädagogin",
    image: franziskaWalter,
    bio: "Als ausgebildete Sängerin und Musikpädagogin spielt Musik schon immer eine zentrale Rolle in meinem Leben.\n\nGut zehn Jahre war ich hauptberuflich Sängerin und festes Ensemblemitglied der Oper Halle und der Deutschen Oper am Rhein Düsseldorf/Duisburg. Daneben habe ich als Musikpädagogin und Stimmbildnerin mit Kindern, Jugendlichen und Erwachsenen gearbeitet. Ich habe Weiterbildungen absolviert, Kurse besucht und meinen beruflichen Mittelpunkt in der Musik und der Arbeit mit Musik gefunden.\n\nMit der Geburt meiner eigenen Kinder wurde für mich konkret greifbar, was ich davor nur erahnt hatte:\n\nder Schatz, der Reichtum an Musik, steckt von Beginn an in jedem einzelnen Kind und sollte jedem zugänglich gemacht werden."
  },
  {
    name: "Laura Isabel Biastoch",
    role: "Sängerin & Musikpädagogin",
    image: lauraIsabelBiastoch,
    bio: "Sängerin und Musikpädagogin für Elementare Musikpraxis, Rhythmik und kreativen Kindertanz.\n\nStudium an der Hochschule für Musik, Theater und Medien Hannover: Gesang, Elementare Musikpädagogik und Rhythmik\n\nAusbildung zur kreativen Kindertanzlehrerin\n\nMusikpädagogin in Musikschulen sowie in musikvermittelnden Projekten\n\nDozentin für musikpädagogische Fort- und Weiterbildungen in Kitas, Berufsbildenden Schulen, Allgemeinbildenden Schulen sowie Musikschulen\n\nGründerin von \"Laura's Rhythmik-Koffer\" (musikpädagogisches Online-Angebot); Digital gestütztes Lernen für Kinder im Bereich Elementare Musikpraxis, Rhythmik und kreativer Kindertanz (Empfohlen vom Verband deutscher Musikschulen, Nominierung für den Leopold-Preis)."
  },
  {
    name: "Helen Simicev",
    role: "Musikgartenlehrerin & Kinderliedermacherin",
    image: helenSimicev,
    bio: "Obwohl ich von klein auf leidenschaftlich gern musizierte, machte ich nach dem Abi erstmal eine kaufmännische Ausbildung, merkte jedoch bald, dass ich meine Liebe zur Musik auch beruflich leben wollte. Nach einigen Jahren Gesangsunterricht in Pop-, Musical- und Jazzgesang, Gesang in Chören, Bands, Musicals und auf Hochzeiten konnte ich meine Leidenschaft 2009 endlich zum Beruf machen. Beim IFEM Mainz absolvierte ich eine Fortbildung zur Musikgartenlehrkraft für Kinder unter 3 Jahren, später dann noch für Kinder bis 5 Jahren und Babys.\n\nIm Jahr 2015 schrieb ich dann meine ersten, eigenen Kinderlieder, die durch die vorangegangenen Musikgartenjahre, viele prägende Eindrücke aus dieser Zeit und die Liebe zur Musik und zur Arbeit mit Kindern inspiriert waren.\n\nIm Jahr 2018 erschien mein erstes Buch \"Guten Morgen, Rasselbande!\" beim Verlag an der Ruhr. Seitdem entstehen immer wieder neue Kinderlieder, Klatschverse und Fingerspiele, die auch auf Spotify zu finden sind. Weitere Infos, Videos und Hörproben von mir gibt's auf www.helensmusic.net, auf meinem YouTube-Kanal, sowie auf Instagram und Facebook unter Helens Kindermusik."
  },
  {
    name: "Frisbi Jones",
    role: "Musiker",
    image: frisbiJones,
    bio: "Musik für große und kleine Menschen."
  },
  {
    name: "Suli Pushban",
    role: "Kinderliedermacherin",
    image: suliPushban,
    bio: "Inhalt folgt..."
  },
  {
    name: "Ida Danzberg",
    role: "Musikpädagogin & Kinderchorleiterin",
    image: idaDanzberg,
    bio: "Ich hatte das Glück, in einer sehr musikalischen Familie aufzuwachsen. Meine Geschwister und ich sangen den ganzen Tag, am liebsten auf langen Autofahrten. Viele meiner Familienmitglieder waren im Chor oder spielten ein Instrument, doch nur wenige machten ihre Leidenschaft zum Beruf. Und ich? Ich wollte ursprünglich \"irgendetwas mit Kunst\" studieren. Den Wunsch, Musik zu studieren, bewegte ich erst kurz nach dem Abitur.\n\nSo führte mich mein Weg an die Hochschule für Musik, Theater und Medien Hannover. Hier absolvierte ich den Bachelor Elementare Musikpädagogik und den Master Kinder- und Jugendchorleitung – jeweils mit Hauptfach Gesang. Mit einigen Kommiliton*innen gründete ich das inzwischen international preisgekrönte Vokalensemble VOKTETT HANNOVER, mit dem ich deutschlandweit in zahlreichen, klassischen A-cappella-Konzerten zu erleben bin.\n\nSeit vielen Jahren unterrichte ich musikalische Früherziehung, arbeite also Musiklehrerin und leite verschiedene Kinder – Jugendchöre. Darüber hinaus dozierte ich in verschiedenen Institutionen rund um das Thema \"Kinder- und Jugendstimme\" – unter anderem bei Singende Kindergärten, einer Initiative von dm Zukunftsmusiker, im Bachelor-Studiengang Elementare Musikpädagogik an der HMTM Hannover und im Lehrgang Elementare Musikpraxis (EMP) an der Thüringer Landesmusikakademie.\n\nEs ist mir ein wichtiges Anliegen, das traditionelle Liedgut zu erhalten und vielfältig zu nutzen, um das Singen und Musizieren zu Hause und in Kindergärten zu beleben und zu fördern. Mein besonderes Interesse liegt im Bereich Stimmbildung und -pflege durch eine gesunde und kindgerechte Singlage und einen freien, kreativen und lustvollen Umgang mit der Stimme.\n\nIm Frühjahr 2020 entwickelte ich das YouTube-Format Ida im Liederkasten und begann damit, Kinderlieder zu schreiben, die Kinder in ihrer Erlebniswelt abholen und den familiären Alltag begleiten sollen. Für die Deutsche Chorjugend gestaltete ich zudem die YouTube-Reihe Sing mit Ida Kinderlieder."
  },
  {
    name: "Andreas Schoba",
    role: "Liedermacher",
    website: "www.liedermacher-schoba.de",
    image: andreasSchoba,
    bio: "2019 hat alles begonnen. Damals war der Liedermacher Andreas Schoba auf der Suche nach schöner Kindermusik für seinen (damals) 2,5 jährigen Sohn. Ihm war es besonders wichtig, handgemachte Kinderlieder zu finden. Also ging er auf die Suche. Irgendwann beschloss er dann aber selbst seine Gitarre in die Hand zu nehmen und sich die ersten Liedtexte und Themen für seine ersten Kinderlieder auszudenken. Ihm war von Anfang an wichtig, dass die Lieder eingängig und vom Text einfach zu verstehen sind. Außerdem sollten sie am besten von den Kindern und Eltern, aber auch von Erzieherinnen und Erziehern, Tageseltern usw. leicht nachzuspielen und nachzusingen sein.\n\nAlle Kinderlied-Texte mit Akkorden zum Nachspielen (21 Stück) gibt es übrigens auf seiner Homepage kostenlos zum Download unter: http://www.kinderlieder-einfach-handgemacht.de/."
  },
  {
    name: "Rolf Zuckowski",
    role: "Kinderliedermacher",
    website: "www.musik-fuer-dich.de",
    image: rolfZuckowski,
    bio: "Rolf Zuckowski war unser Interview-Gast im Rahmen des MusiKonzeptages im November 2022."
  },
  {
    name: "Wolfgang Hering",
    role: "Kinderliedermacher & Buchautor",
    image: wolfgangHering,
    bio: "Wolfgang Hering ist in Mainz geboren und lebt in Groß-Gerau. Er begann seine Musiktätigkeit in frühester Jugend im Folk-Club Mainz und tritt seit dem 18. Lebensjahr öffentlich auf.\n\nNachdem er Erziehungswissenschaften an der Philipps-Universität in Marburg mit Schwerpunkt Sozialpädagogik, Medien- und Kulturarbeit studiert hatte, war er ab 1979 hauptberuflich als Jugendpfleger und Bildungsreferent tätig. Seit 1994 ist er Freiberufler als Kinderliedermacher und Buchautor. Er gründete mit Bernd Meyerholz und später seinem Bruder Bernhard Hering das Trio Kunterbunt. Heute tritt er hauptsächlich mit seinen Soloprogrammen auf.\n\nWolfgang Hering hat viele Erfahrungen aus der praktischen Arbeit mit Kindern und Jugendlichen und ist oftmals eingeladen für Seminare und Gastspiele an deutschen Schulen im Ausland. Er ist Mitglied im Bundesverband der Friedrich-Bödecker-Kreise e. V. sowie bei \"Kindermusik.de\", einem Netzwerk von Bands und Liedermacher/innen aus dem deutschsprachigen Raum. Seit 2018 ist er als Vertreter von Urhebern (Texter + Komponisten) in den Verwaltungsrat der VG Musikedition gewählt worden."
  },
  {
    name: "Silke Kratzsch",
    role: "Musikpädagogin",
    website: "www.hoermine-musikwelt.de",
    image: silkeKratzsch,
    bio: "Silke studierte Klavier und Musikpädagogik in Dresden und unterrichtet seit über 20 Jahren Kurse der Musikalischen Früherziehung.\n\nSie leitet neben ihrer Lehrtätigkeit die Abteilung MFE an der Musikschule am Thomas-Mann-Platz in Chemnitz.\n\nGemeinsam mit Claudia Gundlach hat sie das Programm \"Anton und Hörmine\" entwickelt und gibt ihre Erfahrungen und ihr Konzept auf Fortbildungsseminaren an andere Lehrkräfte und Erzieher:innen weiter."
  },
  {
    name: "Claudia Gundlach",
    role: "Musikpädagogin",
    website: "www.hoermine-musikwelt.de",
    image: claudiaGundlach,
    bio: "...eröffnete nach ihrem Gesangsstudium in Leipzig eine eigene Kindermusikschule in Dresden.\n\nIn über 15 Jahren musikpädagogischer Tätigkeit in Schulen und Kindergärten sowie als Kinderchorleiterin in Berlin sammelte sie viele Erfahrungen, die sie zum Schreiben zahlreicher Lieder, Verse und Kindermusicals inspiriert haben.\n\nGemeinsam mit Silke Kratzsch hat sie das Programm \"Anton und Hörmine\" entwickelt und gibt ihre Erfahrungen und ihr Konzept auf Fortbildungsseminaren an andere Lehrkräfte und Erzieher:innen weiter."
  },
  {
    name: "Dörte Langwald",
    role: "Dozentin für kindliche Entwicklung",
    image: doerteLangwald,
    bio: "Dörte Langwald ist Dozentin für kindliche Entwicklung sowie für therapeutische Fitness & Tanz.\n\nSie gibt Fortbildungen für pädagogische und therapeutische Fachleute rund um die Themen Musik, Bewegung, Tanz und Gesundheit und hat sich dabei auf Säuglinge, Kinder und Frauen spezialisiert.\n\nAn ihrem Wohnort Augsburg gibt sie Kurse für Baby Massage, Baby Gymnastik, Kindertanz, Kinderturnen, Musikalische Früherziehung, Säuglingspflege sowie Fitness für Schwangere & Mamas.\n\nIn ihren Kursen und Seminaren steht im Fokus, Bewegung spielerisch zu vermitteln und dabei die kindlichen Entwicklungsschritte im Blick zu haben, um diese sinnvoll zu unterstützen.\n\nLieder, Reime und Sinnesanregungen werden dabei so umgesetzt, dass sie der Motorik und Wahrnehmung der jeweiligen Altersgruppe entsprechen. Damit die Texte zu den Praxis-Anregungen passen, reimt Dörte mit großer Leidenschaft selber.\n\nDörtes Seminare und Webinare im Internet: www.linktr.ee/doertefit\n\nInstagram: www.instagram.com/mein_kinderreich"
  }
];

const UeberUns = () => {
  const [selectedDozent, setSelectedDozent] = useState<typeof dozenten[0] | null>(null);
  const [selectedTeamMember, setSelectedTeamMember] = useState<typeof bueroTeam[0] | null>(null);
  const [selectedFreund, setSelectedFreund] = useState<typeof freundeMitwirkende[0] | null>(null);

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background">
      <SEO title={`Über uns – Team & Vision | MusiKonzept`} description={`Lerne das Team und die Geschichte hinter MusiKonzept kennen – Online-Akademie für musikalische Bildung und pädagogische Praxis.`} path="/ueber-uns" />
      <Header />
      <main className="pt-20">
        {/* Hero Section - Compact */}
        <section className="relative py-16 md:py-20 overflow-hidden">
          {/* Animated Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-accent/5 to-primary/5">
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNiIgc3Ryb2tlPSJyZ2JhKDI1NSwgMjU1LCAyNTUsIDAuMDUpIi8+PC9nPjwvc3ZnPg==')] opacity-30 animate-pulse"></div>
          </div>
          
          <div className="container mx-auto px-6 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-4 text-sm px-4 py-1.5 bg-accent/10 hover:bg-accent/20 text-accent border border-accent/20 animate-fade-in shadow-sm">
                Die MusiKonzept-Story
              </Badge>
              <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-4 animate-fade-in" style={{ animationDelay: "0.1s" }}>
                Über uns
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed animate-fade-in" style={{ animationDelay: "0.2s" }}>
                Von einer Idee im Jahr 1999 zu einer Plattform, mit der {FACHKRAEFTE_TEXT} Musik in ihren Alltag mit Kindern bringen
              </p>
            </div>
          </div>
        </section>

        {/* Timeline Section - Minimalist with embedded photo */}
        <section className="relative py-16 overflow-hidden">
          {/* Gradient Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-accent/5 via-primary/5 to-accent/10"></div>
          
          <div className="container mx-auto px-6 relative z-10">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12 animate-fade-in">
                <h2 className="text-3xl md:text-4xl font-bold mb-3">
                  Unsere <span className="text-accent">Reise</span>
                </h2>
                <p className="text-muted-foreground">
                  Jeder Meilenstein hat uns zu dem gemacht, was wir heute sind
                </p>
              </div>

              {/* Embedded Collage Image - Smaller */}
              <div className="mb-12 animate-fade-in" style={{ animationDelay: "0.1s" }}>
                <div className="relative group max-w-2xl mx-auto">
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-accent/10 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-500 opacity-50"></div>
                  <img
                    src={collageImage}
                    alt="MusiKonzept Team und Materialien"
                    className="relative rounded-2xl shadow-lg w-full h-auto transform group-hover:scale-[1.02] transition-transform duration-500"
                  />
                </div>
              </div>

              {/* Timeline - Minimalist */}
              <div className="relative">
                {/* Vertical Line */}
                <div className="absolute left-6 md:left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary via-accent to-primary opacity-30"></div>

                {/* Timeline Items */}
                <div className="space-y-8">
                  {timelineData.map((item, index) => {
                    const Icon = item.icon;
                    return (
                      <div
                        key={index}
                        className="relative animate-fade-in group"
                        style={{ animationDelay: `${index * 0.08}s` }}
                      >
                        <div className="flex gap-6 items-start">
                          {/* Year + Icon - Compact */}
                          <div className="flex-shrink-0">
                            <div className={`w-12 h-12 md:w-14 md:h-14 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center shadow-md group-hover:shadow-lg group-hover:scale-110 transition-all duration-300 relative z-10`}>
                              <Icon className="w-5 h-5 md:w-6 md:h-6 text-white" />
                            </div>
                          </div>

                          {/* Content - Simplified */}
                          <div className="flex-1 pb-2">
                            <div className="flex flex-wrap items-baseline gap-3 mb-2">
                              <span className="text-2xl md:text-3xl font-bold text-primary group-hover:text-accent transition-colors duration-300">
                                {item.year}
                              </span>
                              <h3 className="text-lg md:text-xl font-semibold text-foreground flex-1">
                                {item.title}
                              </h3>
                            </div>
                            <p className="text-sm md:text-base text-muted-foreground leading-relaxed mb-3">
                              {item.description}
                            </p>
                            <span className="inline-block px-3 py-1 rounded-lg bg-gradient-to-r from-primary/10 to-accent/10 text-primary text-xs md:text-sm font-medium border border-primary/20">
                              {item.highlight}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section - Moved Down */}
        <section className="relative py-16 overflow-hidden">
          {/* Gradient Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-accent/5 to-primary/10"></div>
          
          <div className="container mx-auto px-6 relative z-10">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-3 animate-fade-in">
                MusiKonzept in <span className="text-accent">Zahlen</span>
              </h2>
              <p className="text-center text-muted-foreground mb-10 animate-fade-in" style={{ animationDelay: "0.1s" }}>
                Unsere Erfolgsgeschichte spricht für sich
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 animate-fade-in" style={{ animationDelay: "0.2s" }}>
                <div className="bg-background/80 backdrop-blur-sm rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-border/50 hover:border-primary/50 group">
                  <div className="text-4xl md:text-5xl font-bold bg-gradient-to-br from-primary to-primary/70 bg-clip-text text-transparent mb-2 group-hover:scale-110 transition-transform duration-300">
                    1999
                  </div>
                  <div className="text-sm text-muted-foreground">gegründet als gitarrissimo</div>
                </div>
                <div className="bg-background/80 backdrop-blur-sm rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-border/50 hover:border-accent/50 group">
                  <div className="text-4xl md:text-5xl font-bold bg-gradient-to-br from-accent to-accent/70 bg-clip-text text-transparent mb-2 group-hover:scale-110 transition-transform duration-300">
                    {FACHKRAEFTE_KURZ}
                  </div>
                  <div className="text-sm text-muted-foreground">Fachkräfte</div>
                </div>
                <div className="bg-background/80 backdrop-blur-sm rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-border/50 hover:border-primary/50 group">
                  <div className="text-4xl md:text-5xl font-bold bg-gradient-to-br from-primary to-accent bg-clip-text text-transparent mb-2 group-hover:scale-110 transition-transform duration-300">
                    {LEKTIONEN_KURZ}
                  </div>
                  <div className="text-sm text-muted-foreground">Lektionen</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Dozenten Section */}
        <section className="relative py-16 overflow-hidden">
          {/* Gradient Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-accent/10 to-primary/5"></div>
          
          <div className="container mx-auto px-6 relative z-10">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-3 animate-fade-in text-center">
                Dozent:innen
              </h2>
              <p className="text-center text-muted-foreground mb-10 animate-fade-in" style={{ animationDelay: "0.1s" }}>
                Lerne unser engagiertes Team kennen
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {dozenten.map((dozent, index) => (
                  <div
                    key={index}
                    onClick={() => setSelectedDozent(dozent)}
                    className="group cursor-pointer animate-fade-in"
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    <div className="relative bg-card rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border border-border/50 hover:border-accent/70">
                      {/* Glow Effect */}
                      <div className="absolute inset-0 bg-gradient-to-br from-accent/0 via-accent/0 to-accent/0 group-hover:from-accent/20 group-hover:via-accent/10 group-hover:to-primary/20 transition-all duration-500 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 -z-10"></div>
                      
                      {/* Shimmer Effect */}
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out"></div>
                      
                      <div className="aspect-[4/3] overflow-hidden relative">
                        <div className="absolute inset-0 bg-gradient-to-t from-accent/80 via-accent/20 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500 z-10"></div>
                        <img
                          src={dozent.image}
                          alt={dozent.name}
                          className="w-full h-full object-cover group-hover:scale-110 group-hover:brightness-110 transition-all duration-700"
                        />
                      </div>
                      <div className="p-5 relative">
                        <h3 className="text-lg font-bold text-foreground mb-1 group-hover:text-accent transition-colors duration-300 group-hover:translate-x-1">
                          {dozent.name}
                        </h3>
                        <p className="text-sm text-muted-foreground group-hover:text-foreground/80 transition-colors duration-300">{dozent.role}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Büro-Team Section */}
        <section className="relative py-16 overflow-hidden">
          {/* Gradient Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-accent/5 via-primary/5 to-accent/10"></div>
          
          <div className="container mx-auto px-6 relative z-10">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-3 animate-fade-in text-center">
                Büro-Team
              </h2>
              <p className="text-center text-muted-foreground mb-10 animate-fade-in" style={{ animationDelay: "0.1s" }}>
                Die Menschen hinter den Kulissen
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {bueroTeam.map((member, index) => (
                  <div
                    key={index}
                    onClick={() => setSelectedTeamMember(member)}
                    className="group cursor-pointer animate-fade-in"
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    <div className="relative bg-card rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border border-border/50 hover:border-primary/70">
                      {/* Glow Effect */}
                      <div className="absolute inset-0 bg-gradient-to-br from-primary/0 via-primary/0 to-primary/0 group-hover:from-primary/20 group-hover:via-primary/10 group-hover:to-accent/20 transition-all duration-500 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 -z-10"></div>
                      
                      {/* Shimmer Effect */}
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out"></div>
                      
                      <div className="aspect-[4/3] overflow-hidden relative">
                        <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-primary/20 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500 z-10"></div>
                        <img
                          src={member.image}
                          alt={member.name}
                          className="w-full h-full object-cover group-hover:scale-110 group-hover:brightness-110 transition-all duration-700"
                        />
                      </div>
                      <div className="p-5 relative">
                        <h3 className="text-lg font-bold text-foreground mb-1 group-hover:text-primary transition-colors duration-300 group-hover:translate-x-1">
                          {member.name}
                        </h3>
                        <p className="text-sm text-muted-foreground group-hover:text-foreground/80 transition-colors duration-300">{member.role}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Freunde & Mitwirkende Section */}
        <section className="relative py-16 overflow-hidden">
          {/* Gradient Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-accent/5 to-primary/10"></div>
          
          <div className="container mx-auto px-6 relative z-10">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-3 animate-fade-in text-center">
                Freunde & Mitwirkende
              </h2>
              <p className="text-center text-muted-foreground mb-10 animate-fade-in" style={{ animationDelay: "0.1s" }}>
                Unser Netzwerk aus talentierten Kinderliedermacher:innen
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {freundeMitwirkende.map((person, index) => (
                  <div
                    key={index}
                    onClick={() => setSelectedFreund(person)}
                    className="group cursor-pointer animate-fade-in"
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    <div className="relative bg-card rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border border-border/50 hover:border-accent/70">
                      {/* Glow Effect */}
                      <div className="absolute inset-0 bg-gradient-to-br from-accent/0 via-accent/0 to-accent/0 group-hover:from-accent/20 group-hover:via-primary/10 group-hover:to-accent/20 transition-all duration-500 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 -z-10"></div>
                      
                      {/* Shimmer Effect */}
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out"></div>
                      
                      <div className="aspect-[4/3] overflow-hidden relative">
                        <div className="absolute inset-0 bg-gradient-to-t from-accent/80 via-accent/20 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500 z-10"></div>
                        <img
                          src={person.image}
                          alt={person.name}
                          className="w-full h-full object-cover group-hover:scale-110 group-hover:brightness-110 transition-all duration-700"
                        />
                      </div>
                      <div className="p-5 relative">
                        <h3 className="text-lg font-bold text-foreground mb-1 group-hover:text-accent transition-colors duration-300 group-hover:translate-x-1">
                          {person.name}
                        </h3>
                        <p className="text-sm text-muted-foreground group-hover:text-foreground/80 transition-colors duration-300">{person.role}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      {/* Dozent Dialog */}
      <Dialog open={!!selectedDozent} onOpenChange={() => setSelectedDozent(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          {selectedDozent && (
            <>
              <DialogHeader>
                <DialogTitle className="text-3xl font-bold">{selectedDozent.name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-6">
                <div className="aspect-video rounded-2xl overflow-hidden">
                  <img
                    src={selectedDozent.image}
                    alt={selectedDozent.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <p className="text-xl text-accent font-semibold mb-2">{selectedDozent.role}</p>
                  {selectedDozent.email && (
                    <a 
                      href={`mailto:${selectedDozent.email}`}
                      className="text-primary hover:text-accent transition-colors duration-300 block mb-4"
                    >
                      {selectedDozent.email}
                    </a>
                  )}
                  {selectedDozent.website && (
                    <a 
                      href={`https://${selectedDozent.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:text-accent transition-colors duration-300 block mb-4"
                    >
                      {selectedDozent.website}
                    </a>
                  )}
                  <div className="prose prose-lg max-w-none text-muted-foreground">
                    {selectedDozent.bio.split('\n\n').map((paragraph, index) => (
                      <p key={index} className="mb-4">{paragraph}</p>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Team Member Dialog */}
      <Dialog open={!!selectedTeamMember} onOpenChange={() => setSelectedTeamMember(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          {selectedTeamMember && (
            <>
              <DialogHeader>
                <DialogTitle className="text-3xl font-bold">{selectedTeamMember.name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-6">
                <div className="aspect-video rounded-2xl overflow-hidden">
                  <img
                    src={selectedTeamMember.image}
                    alt={selectedTeamMember.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <p className="text-xl text-primary font-semibold mb-4">{selectedTeamMember.role}</p>
                  <div className="prose prose-lg max-w-none text-muted-foreground">
                    {selectedTeamMember.bio.split('\n\n').map((paragraph, index) => (
                      <p key={index} className="mb-4">{paragraph}</p>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Freunde & Mitwirkende Dialog */}
      <Dialog open={!!selectedFreund} onOpenChange={() => setSelectedFreund(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          {selectedFreund && (
            <>
              <DialogHeader>
                <DialogTitle className="text-3xl font-bold">{selectedFreund.name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-6">
                <div className="aspect-video rounded-2xl overflow-hidden">
                  <img
                    src={selectedFreund.image}
                    alt={selectedFreund.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <p className="text-xl text-accent font-semibold mb-2">{selectedFreund.role}</p>
                  {selectedFreund.website && (
                    <a 
                      href={`https://${selectedFreund.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:text-accent transition-colors duration-300 block mb-4"
                    >
                      {selectedFreund.website}
                    </a>
                  )}
                  <div className="prose prose-lg max-w-none text-muted-foreground">
                    {selectedFreund.bio.split('\n\n').map((paragraph, index) => (
                      <p key={index} className="mb-4 whitespace-pre-line">{paragraph}</p>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UeberUns;
